import styled from "styled-components";
import { useSelector } from "react-redux";
import {
  faUser,
  faPhone,
  faEnvelope,
  faCaretRight,
  faXmarkCircle
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { Collapse, Spinner } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Field from "./FormField";
import {
  FormSubText,
  FormHeadline,
  SubmitButton,
  LoaderContainer,
  FormSubHeadline,
  FormInnerContainer,
  SocialProofLine,
  RatingBadge
} from "./ContactInfoForm.styles";
import {
  ContactPhoneSchema,
  ContactFormBaseSchema,
  ContactFormCompleteSchema
} from "../../services/validation-schema";
import PhoneInputField from "../PhoneInput";
import { PolicySection } from "../PolicySection";
import CustomNextButton from "../CustomNextButton";
import { HeaderImage } from "../Header/Header.styles";
import { getCountryCodes } from "../../services/utils";
import {
  buildReminderLeadDetails,
  useLeadAction
} from "../../hooks/useLeadAction";
import { FORM_IMAGE_PLACEMENT, THUMBNAIL_TYPE } from "../../templates/strings";
import { useEmailSuggestion } from "../../hooks/useEmailSuggestion";
import { generatePolicyScope } from "../../services/helper";
import { useSplitTest } from "../../hooks/useSplitTest";
import {
  NAME_INPUT_PROPS,
  EMAIL_INPUT_PROPS,
  onEnterKey
} from "../../services/form-fields";

const TermsPolicyDiv = styled.div`
  font-size: 10px;
`;

export const ContactInfoForm = ({ setLeadError, upcomingLeadInfo = null }) => {
  const [isLoading, setIsLoading] = useState();
  const { BEFORE, AFTER } = FORM_IMAGE_PLACEMENT;
  const {
    funnel,
    leadForm: { isLandedFromPhoneReminderEmail }
  } = useSelector((state) => state.funnelInfo);
  const reclaimMode = useSelector((state) => state.reclaimMode);
  const { amPrivacyPolicy } = useSelector(
    (state) => state.globalPrivacyPolicyInfo
  );
  const [isSubmittingFirstStep, setIsSubmittingFirstStep] = useState(false);
  const [firstStepSubmissionError, setFirstStepSubmissionError] = useState("");
  const [displayPhoneScreen, setDisplayPhoneScreen] = useState(
    isLandedFromPhoneReminderEmail
  );
  const { emailSuggestion, checkEmail, clearEmailSuggestion } =
    useEmailSuggestion();
  const {
    handleSubmitLeadInfo,
    checkPhoneNumberValidity,
    handleSubmitUpcomingLeadInfo
  } = useLeadAction();
  // Split test (Cockpit): the assigned variant replaces exactly one text
  // element; the exposure is counted once per visitor when the form shows.
  const { textElements, presentation, recordExposure } = useSplitTest(
    funnel.textElements
  );
  const { colours, socialProof, googleRating } = presentation;
  useEffect(() => {
    recordExposure();
  }, [recordExposure]);
  const {
    formScreenHeader,
    formScreenSubtext,
    formHeadline,
    subHeadlineForm,
    fieldTitleName,
    fieldPlaceholderName,
    fieldTitleEmail,
    fieldPlaceholderEmail,
    fieldTitlePhone,
    fieldPlaceholderPhone,
    submitButton,
    submitButtonSubtext,
    numberCheck,
    enablePhoneWithTwoSteps,
    phoneScreenHeading,
    enableFormImage,
    formImage,
    formImagePlacement
  } = textElements;
  const country = funnel.companyInfo?.country;

  // displayPhoneScreen && enablePhoneWithTwoSteps
  //   ? ContactPhoneSchema
  //   : !enablePhoneWithTwoSteps
  //     ? ContactFormCompleteSchema
  //     : ContactFormBaseSchema;

  let schema;
  if (!enablePhoneWithTwoSteps || reclaimMode.enabled) {
    schema = ContactFormCompleteSchema;
  } else if (displayPhoneScreen) {
    schema = ContactPhoneSchema;
  } else {
    schema = ContactFormBaseSchema;
  }

  const policyScope = generatePolicyScope({ funnel, reclaimMode });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      policyCheck: false,
      policySectionDisplayDecider: !!policyScope
    }
  });

  const {
    setError,
    setFocus,
    setValue,
    handleSubmit,
    formState: { errors, isValid }
  } = methods;

  // Step one of the two-step form asks for name and email only
  const isPhoneOnNextStep = enablePhoneWithTwoSteps && !reclaimMode.enabled;

  const handleFirstStepSubmission = (formValues) => {
    if (checkEmail(formValues.email)?.blocking || !isValid) return;
    handleSubmitUpcomingLeadInfo({
      formValues,
      setDisplayNext: setDisplayPhoneScreen,
      setIsSubmitting: setIsSubmittingFirstStep,
      submissionError: setFirstStepSubmissionError
    });
  };

  const submitFirstStep = () => handleSubmit(handleFirstStepSubmission)();

  const onSubmit = async (formValues) => {
    if (checkEmail(formValues.email)?.blocking) return;
    const isValidPhoneNumber = checkPhoneNumberValidity({
      phoneNumber: formValues.phone,
      numberCheck,
      setError
    });
    if (!isValidPhoneNumber) return;

    let upcomingLeadDetails = null;
    // check if lead is coming from phone reminder email
    if (isLandedFromPhoneReminderEmail && upcomingLeadInfo) {
      upcomingLeadDetails = buildReminderLeadDetails(
        upcomingLeadInfo,
        formValues.phone
      );
    }
    handleSubmitLeadInfo({
      formValues,
      setLeadError,
      setIsLoading,
      upcomingLeadDetails,
      amPrivacyPolicy
    });
  };

  return (
    <div>
      <div
        className="p-2 border border-1 border-white rounded-top text-center text-white"
        style={{ backgroundColor: colours.formHeaderBackground }}
      >
        <p className="mb-0 fw-bolder">{formScreenHeader}</p>
        <FormSubText className="mb-0">{formScreenSubtext}</FormSubText>
      </div>
      <FormInnerContainer
        className={`px-3 pt-3 mx-3 px-xsm-3 px-sm-3 px-xl-5 fs-6 ${
          isLoading ? "pe-none" : ""
        }`}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={(event) => {
              handleSubmit(onSubmit)(event);
            }}
          >
            {!displayPhoneScreen ? (
              <>
                <FormHeadline className="text-center px-5 fw-bolder fs-5">
                  {formHeadline}
                </FormHeadline>
                <div
                  className={`d-flex ${
                    formImagePlacement === BEFORE
                      ? "flex-column-reverse"
                      : formImagePlacement === AFTER
                        ? "flex-column"
                        : ""
                  }`}
                >
                  <FormSubHeadline className="text-center fs-7">
                    {subHeadlineForm}
                  </FormSubHeadline>
                  {enableFormImage && formImage?.key && (
                    <div className="text-center mb-3">
                      <HeaderImage
                        alt="form-image"
                        src={`${process.env.REACT_APP_ASSET_CDN_URL}/${formImage.key}/${THUMBNAIL_TYPE.COMPANY_LOGO}`}
                      />
                    </div>
                  )}
                </div>
                {/* own block below the sub-headline: the container above is a
                    flex row without an image placement */}
                {socialProof?.enabled && socialProof.label && (
                  <SocialProofLine data-testid="social-proof">
                    {socialProof.label}
                  </SocialProofLine>
                )}
                <Field
                  field={{
                    name: "name",
                    type: "text",
                    icon: faUser,
                    title: fieldTitleName,
                    hasError: errors?.name,
                    placeholder: fieldPlaceholderName,
                    inputProps: {
                      ...NAME_INPUT_PROPS,
                      enterKeyHint: "next",
                      onKeyDown: onEnterKey(() => setFocus("email"))
                    }
                  }}
                />
                <Field
                  field={{
                    name: "email",
                    type: "email",
                    icon: faEnvelope,
                    title: fieldTitleEmail,
                    hasError: errors?.email,
                    placeholder: fieldPlaceholderEmail,
                    inputProps: {
                      ...EMAIL_INPUT_PROPS,
                      enterKeyHint: isPhoneOnNextStep ? "go" : "next",
                      onKeyDown: onEnterKey(
                        isPhoneOnNextStep
                          ? submitFirstStep
                          : () => setFocus("phone")
                      )
                    },
                    onBlur: (event) => checkEmail(event.target.value),
                    // Once a hint is shown, keep it in sync while correcting
                    onChange: (event) =>
                      emailSuggestion && checkEmail(event.target.value)
                  }}
                  suggestion={emailSuggestion}
                  onApplySuggestion={() => {
                    setValue("email", emailSuggestion.email, {
                      shouldValidate: true
                    });
                    clearEmailSuggestion();
                  }}
                />
                {isPhoneOnNextStep && (
                  <div>
                    <TermsPolicyDiv className="text-secondary">
                      *Mit dem Klick auf den “Weiter” Button stellen Sie eine
                      Anfrage und akzeptieren unsere AGB und
                      Datenschutzerklärung. Wir kontaktieren Sie ausschließlich
                      zur Vervollständigung Ihrer Angaben. Ein kostenloser
                      Widerspruch ist jederzeit möglich.
                    </TermsPolicyDiv>
                    <div className="my-3">
                      <CustomNextButton
                        handleClick={submitFirstStep}
                        nextBtnOpts={textElements}
                        isDisabled={isSubmittingFirstStep}
                        isForwarding={isSubmittingFirstStep}
                        fullWidth
                      />
                    </div>
                    <Collapse
                      in={
                        !isSubmittingFirstStep &&
                        firstStepSubmissionError !== ""
                      }
                    >
                      <div>
                        <div className="py-2 bg-danger text-white rounded ps-2">
                          <FontAwesomeIcon
                            icon={faXmarkCircle}
                            className="me-2"
                          />
                          {firstStepSubmissionError}
                        </div>
                      </div>
                    </Collapse>
                  </div>
                )}
              </>
            ) : (
              <FormHeadline className="text-center px-5 fw-bolder fs-5 mt-3 mb-4">
                {phoneScreenHeading}
              </FormHeadline>
            )}
            {(displayPhoneScreen ||
              !enablePhoneWithTwoSteps ||
              reclaimMode.enabled) && (
              <>
                <PhoneInputField
                  field={{
                    countries: getCountryCodes(country),
                    icon: faPhone,
                    name: "phone",
                    title: fieldTitlePhone,
                    hasError: errors?.phone,
                    placeholder: fieldPlaceholderPhone
                  }}
                />
                {policyScope && <PolicySection policyScope={policyScope} />}
                {googleRating?.enabled && googleRating.rating && (
                  <RatingBadge data-testid="google-rating">
                    <span className="stars" aria-hidden="true">
                      {"★★★★★".slice(0, Math.round(googleRating.rating))}
                    </span>
                    <span className="fw-bolder">
                      {googleRating.rating.toLocaleString("de-DE", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1
                      })}
                    </span>
                    <span>· {googleRating.count} Google-Bewertungen</span>
                  </RatingBadge>
                )}
                <SubmitButton
                  type="submit"
                  $bg={colours.submitButtonBackground}
                  className="w-100 mt-3 border-0 outline-0"
                  disabled={isLoading || Object.keys(errors)?.length}
                >
                  <div>
                    <div className="fs-5 fw-bolder">
                      {submitButton}
                      <FontAwesomeIcon icon={faCaretRight} className="ms-2" />
                    </div>
                    <div style={{ fontSize: "14px", opacity: "0.7" }}>
                      {submitButtonSubtext}
                    </div>
                  </div>
                  {isLoading && (
                    <LoaderContainer>
                      <Spinner />
                    </LoaderContainer>
                  )}
                </SubmitButton>
              </>
            )}
          </form>
        </FormProvider>
      </FormInnerContainer>
    </div>
  );
};
